import { EMPTY_ATTR_FILTERS } from "../../utils/mapFilters";
import { MAP_LAYER_COLORS, MAP_LAYER_LABELS } from "../../constants/mapLayers";

// Every pin type, grouped. These checkboxes are the single home for "what is
// drawn" — the rail's Layers popover was a second entry point onto the same
// state and has been removed in favour of this.
// Outreach is deliberately NOT under Deal stage. It is the sourcing track —
// LOIs the Outreach tab fired — running parallel to the pipeline, not a rung
// on it. Grouping it with Tracking/Screening/Underwriting read as though a
// deal progressed through it, and it also dwarfed the real stages in the
// count column. It sits on its own below Comps instead.
const STAGE_KEYS = Object.keys(MAP_LAYER_LABELS)
  .filter(k => !k.startsWith("db_") && !k.startsWith("comp_") && k !== "outreach" && k !== "sourcing");

const LAYER_GROUPS = [
  { title: "Deal stage", keys: STAGE_KEYS },
  { title: "Comps",      keys: ["comp_sale", "comp_lease"] },
  { title: "Outreach & Sourcing", keys: ["outreach", "sourcing"] },
];

// ═══════════════════════════════════════════════════════════════════════════
// FILTERS PANEL
// ═══════════════════════════════════════════════════════════════════════════
// Everything that narrows the pin set except geography, which is the
// Location panel's job. Two kinds of control: which pin types are drawn
// (deal stage, comps) and attribute ranges over columns the remaining
// records actually carry — SF, year built, cap rate, lease type.
//
// Class, % leased and for-sale status used to live here. They were columns
// on the sourcing properties table, and with sourcing pins removed nothing
// on the map carries them — so those controls would have matched zero pins.

const LEASE_TYPES = ["NNN", "Modified Gross", "Gross"];

export default function MapFiltersPanel({
  S, filters, onChange, onClose, resultCount, layers, setLayers, layerCounts,
}) {
  const f = filters;
  const set = (key, value) => onChange({ ...f, [key]: value });

  const toggleIn = (key, value) => {
    const list = f[key] || [];
    set(key, list.includes(value) ? list.filter(x => x !== value) : [...list, value]);
  };

  const input = {
    background: S.card, border: `1px solid ${S.border}`, borderRadius: 6,
    padding: "6px 8px", color: S.text, fontSize: 11, fontFamily: S.font,
    outline: "none", width: "100%", boxSizing: "border-box",
  };

  const chip = (active) => ({
    background: active ? S.red : "transparent",
    border: `1px solid ${active ? S.red : S.border}`,
    borderRadius: 6, color: active ? "#ffffff" : S.text,
    padding: "5px 11px", fontSize: 10.5, fontWeight: 800,
    fontFamily: S.font, cursor: "pointer", whiteSpace: "nowrap",
  });

  const Row = ({ label, note, children }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: S.muted, marginBottom: 5 }}>
        {label}
        {note && <span style={{ fontWeight: 600, letterSpacing: 0, textTransform: "none", marginLeft: 6, opacity: 0.8 }}>{note}</span>}
      </div>
      {children}
    </div>
  );

  const pair = (minKey, maxKey, placeholderMin, placeholderMax, step) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input type="number" step={step} value={f[minKey]} placeholder={placeholderMin}
        onChange={e => set(minKey, e.target.value)} style={input} />
      <span style={{ color: S.muted, fontSize: 11 }}>to</span>
      <input type="number" step={step} value={f[maxKey]} placeholder={placeholderMax}
        onChange={e => set(maxKey, e.target.value)} style={input} />
    </div>
  );

  return (
    <div style={{
      position: "absolute", top: "calc(100% + 6px)", right: 0, width: 380,
      maxHeight: "70vh", overflowY: "auto",
      background: S.sidebar, border: `1px solid ${S.border}`, borderRadius: 10,
      boxShadow: "0 12px 36px rgba(0,0,0,0.45)", zIndex: 1100,
      fontFamily: S.font, color: S.text,
    }}>
      <div style={{ padding: "12px 15px", borderBottom: `1px solid ${S.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: S.sidebar, zIndex: 1 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800 }}>Filters</div>
          <div style={{ fontSize: 10, color: S.muted, marginTop: 1 }}>
            {resultCount.toLocaleString()} pin{resultCount === 1 ? "" : "s"} match
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 16, padding: 4 }}>✕</button>
      </div>

      <div style={{ padding: "14px 15px" }}>
        {/* Layer visibility — deal stages, comps and sourcing buckets. */}
        {layers && setLayers && LAYER_GROUPS.map(g => {
          const allOn = g.keys.every(k => layers[k]);
          const hidden = g.keys.filter(k => !layers[k]).length;
          return (
            <Row key={g.title} label={g.title} note={hidden > 0 ? `${hidden} hidden` : null}>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, marginBottom: 6 }}>
                {g.keys.map(k => {
                  const on = !!layers[k];
                  const color = MAP_LAYER_COLORS[k] || "#6a6a6a";
                  const label = (MAP_LAYER_LABELS[k] || k).replace(g.strip || "", "");
                  return (
                    <label key={k}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", color: on ? S.text : S.muted }}>
                      <input type="checkbox" checked={on}
                        onChange={() => setLayers(l => ({ ...l, [k]: !l[k] }))}
                        style={{ accentColor: color, cursor: "pointer" }} />
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <span style={{ flex: 1 }}>{label}</span>
                      <span style={{ fontSize: 10, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>
                        {(layerCounts?.[k] || 0).toLocaleString()}
                      </span>
                    </label>
                  );
                })}
              </div>
              <button
                onClick={() => setLayers(l => {
                  const next = { ...l };
                  g.keys.forEach(k => { next[k] = !allOn; });
                  return next;
                })}
                style={{ background: "transparent", border: `1px solid ${S.border}`, borderRadius: 6, color: S.muted, padding: "4px 9px", fontSize: 10, fontWeight: 800, fontFamily: S.font, cursor: "pointer" }}>
                {allOn ? "Uncheck all" : "Check all"}
              </button>
            </Row>
          );
        })}

        <Row label="Building SF">
          {pair("sfMin", "sfMax", "Min", "Max", "1000")}
        </Row>

        <Row label="Year built">
          {pair("yearMin", "yearMax", "From", "To", "1")}
        </Row>

        <Row label="Cap rate %">
          {pair("capMin", "capMax", "Min", "Max", "0.01")}
        </Row>

        <Row label="Lease type" note="lease comps">
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {LEASE_TYPES.map(t => (
              <button key={t} onClick={() => toggleIn("leaseTypes", t)}
                style={chip((f.leaseTypes || []).includes(t))}>{t}</button>
            ))}
          </div>
        </Row>

      </div>

      <div style={{ padding: "11px 15px", borderTop: `1px solid ${S.border}`, display: "flex", gap: 7, position: "sticky", bottom: 0, background: S.sidebar }}>
        <button onClick={() => onChange({ ...EMPTY_ATTR_FILTERS })}
          style={{ flex: 1, background: "transparent", border: `1px solid ${S.border}`, borderRadius: 7, color: S.muted, padding: "7px 9px", fontSize: 10.5, fontWeight: 800, fontFamily: S.font, cursor: "pointer" }}>
          Clear
        </button>
        <button onClick={onClose}
          style={{ flex: 1, background: S.red, border: "none", borderRadius: 7, color: "#ffffff", padding: "7px 9px", fontSize: 10.5, fontWeight: 800, fontFamily: S.font, cursor: "pointer" }}>
          Done
        </button>
      </div>
    </div>
  );
}
